import React from 'react';

const AffiliateLink = ({ 
  linkType, 
  merchantId, 
  planId, 
  children, 
  className, 
  fallbackUrl,
  onClick 
}) => {
  const [affiliateLink, setAffiliateLink] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchAffiliateLink = async () => {
      if (!merchantId) {
        console.log('No merchantId provided, skipping affiliate link fetch');
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // First try to get plan-specific affiliate link
        const params = new URLSearchParams({
          merchant_id: merchantId,
        });
        
        if (linkType) {
          params.append('link_type', linkType);
        }
        
        if (planId !== undefined && planId !== null) {
          params.append('plan_id', planId);
        }
        
        console.log('Fetching plan-specific affiliate links with params:', params.toString());
        
        const apiUrl = `https://easysubs-1ad308994ada.herokuapp.com/api/merchant-affiliate-links?${params}`;
        console.log('API URL:', apiUrl);
        
        const response = await fetch(apiUrl);
        
        console.log('Response status:', response.status, response.statusText);
        
        if (response.ok) {
          const links = await response.json();
          console.log('Received affiliate links:', links);
          
          if (links.length > 0) {
            console.log('Setting plan-specific affiliate link:', links[0]);
            setAffiliateLink(links[0]);
          } else {
            // If no plan-specific link found, try to get merchant-level link (plan_id: null)
            console.log('No plan-specific affiliate links found, trying merchant-level link');
            
            const merchantParams = new URLSearchParams({
              merchant_id: merchantId,
              plan_id: 'null'
            });
            
            if (linkType) {
              merchantParams.append('link_type', linkType);
            }
            
            const merchantApiUrl = `https://easysubs-1ad308994ada.herokuapp.com/api/merchant-affiliate-links?${merchantParams}`;
            console.log('Trying merchant-level API URL:', merchantApiUrl);
            
            const merchantResponse = await fetch(merchantApiUrl);
            
            if (merchantResponse.ok) {
              const merchantLinks = await merchantResponse.json();
              console.log('Received merchant-level affiliate links:', merchantLinks);
              
              if (merchantLinks.length > 0) {
                console.log('Setting merchant-level affiliate link:', merchantLinks[0]);
                setAffiliateLink(merchantLinks[0]);
              } else {
                console.log('No affiliate links found at all');
                setError('No affiliate links found');
              }
            } else {
              const errorText = await merchantResponse.text();
              console.error('Merchant-level API error:', merchantResponse.status, errorText);
              setError(`API error: ${merchantResponse.status} ${errorText}`);
            }
          }
        } else {
          const errorText = await response.text();
          console.error('API error:', response.status, errorText);
          setError(`API error: ${response.status} ${errorText}`);
        }
      } catch (error) {
        console.error('Error fetching affiliate link:', error);
        setError(`Network error: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAffiliateLink();
  }, [merchantId, planId, linkType]);

  const handleClick = async (e) => {
    if (onClick) {
      onClick(e);
    }
    
    if (affiliateLink) {
      // Track the click
      try {
        const userId = localStorage.getItem("userID");
        console.log('Tracking affiliate click:', {
          affiliate_link_id: affiliateLink._id,
          user_id: userId,
          merchant_id: merchantId,
          plan_id: planId
        });
        
        const trackResponse = await fetch('https://easysubs-1ad308994ada.herokuapp.com/api/track-affiliate-click', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            affiliate_link_id: affiliateLink._id,
            user_id: userId,
            merchant_id: merchantId,
            plan_id: planId
          }),
        });
        
        console.log('Tracking response:', trackResponse.status);
      } catch (error) {
        console.error('Error tracking affiliate click:', error);
      }
      
      // Open the affiliate link in a new tab
      window.open(affiliateLink.url, '_blank');
      e.preventDefault();
    } else {
      console.log('No affiliate link available, opening fallback');
    }
  };

  if (loading) {
    console.log('Affiliate link loading...');
    return <div className={className}>{children}</div>;
  }

  if (error) {
    console.log('Affiliate link error:', error);
  }

  if (affiliateLink) {
    console.log('Rendering affiliate link:', affiliateLink.url);
    return (
      <a
        href={affiliateLink.url}
        className={className}
        onClick={handleClick}
        rel={affiliateLink.attributes?.rel || 'sponsored'}
        target={affiliateLink.attributes?.target || '_blank'}
      >
        {children}
      </a>
    );
  }

  // Fallback to regular link if no affiliate link found
  console.log('Falling back to regular link:', fallbackUrl);
  return (
    <a href={fallbackUrl} className={className} target="_blank" rel="noopener">
      {children}
    </a>
  );
};

export default AffiliateLink;